import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, IconButton } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios/axios-instance";
import CancelButton from "../../components/common/buttons/CancelButton";
import Card from "../../components/common/Card";
import SelectField from "../../components/common/fields/SelectField";
import MuiTable from "../../components/common/MuiTable";
import { Column } from "../../utils/constants";
import { pathAPI } from "../../utils/pathApi";

const REQUEST_TYPES = ["COUNTRY_CHANGE", "SALON_IMAGES_CHANGE"];
const REQUEST_STATUSES = ["WAITING", "SUPPORTING", "APPROVED", "REJECTED", "CANCELLED"];

const TYPE_FILTER_WIDTH = 220;
const STATUS_FILTER_WIDTH = 180;

const statusStyle = (status: string) => {
  switch (status) {
    case "APPROVED":
      return "bg-green-500";
    case "REJECTED":
    case "CANCELLED":
      return "bg-gray-500";
    case "SUPPORTING":
      return "bg-blue-500";
    default:
      return "bg-red-500";
  }
};

const pageContent = (response: any) => response?.data?.content || response?.data?.data?.content || [];
const totalElements = (response: any) => response?.data?.totalElements || response?.data?.data?.totalElements || 0;

const OutletChangeRequests = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const requestTypes = REQUEST_TYPES.map((value) => ({
    value,
    label: t(`outlet_change_request.type.${value}`),
  }));
  const requestStatuses = REQUEST_STATUSES.map((value) => ({
    value,
    label: t(`outlet_change_request.status.${value}`),
  }));
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [requestType, setRequestType] = useState("");
  const [status, setStatus] = useState("WAITING");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(pathAPI.outletChangeRequests, {
        params: {
          page,
          size,
          request_type: requestType || undefined,
          status: status || undefined,
        },
      });
      const content = pageContent(response).map((item: any) => ({
        ...item,
        outlet_name:
          item.outlet_name ||
          item.outlet?.name ||
          item.outlet?.salon_name ||
          item.outlet_id ||
          "-",
        action: item.id,
      }));
      setRows(content);
      setTotal(totalElements(response));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [page, size, requestType, status]);

  const header: Column[] = [
    { id: "id", label: "ID", minWidth: 40, align: "center" },
    { id: "request_type", format: (value: string) => <div>{value ? t(`outlet_change_request.type.${value}`, { defaultValue: value }) : "-"}</div>, label: t("outlet_change_request.col.type"), minWidth: 160, align: "center" },
    { id: "outlet_name", label: t("outlet_change_request.col.salon"), minWidth: 180, align: "center" },
    {
      id: "status",
      label: t("outlet_change_request.col.status"),
      minWidth: 110,
      align: "center",
      format: (value: string) => (
        <div className={`font-bold px-2 rounded-lg text-white ${statusStyle(value)}`}>{t(`outlet_change_request.status.${value}`, { defaultValue: value })}</div>
      ),
    },
    {
      id: "created_date",
      label: t("outlet_change_request.col.created"),
      minWidth: 150,
      align: "center",
      format: (value: string) => <div>{value ? moment(value).format("YYYY/MM/DD HH:mm:ss") : "-"}</div>,
    },
    {
      id: "action",
      label: t("outlet_change_request.col.actions"),
      minWidth: 100,
      align: "center",
      format: (id: number) => (
        <Box display="flex" justifyContent="center">
          <IconButton onClick={() => navigate(`/outlet-change-requests/${id}`)}>
            <VisibilityIcon color="success" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Card>
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Box width={TYPE_FILTER_WIDTH}>
          <SelectField
            value={requestType}
            emptyLabel={t("outlet_change_request.all_types")}
            placeholder={t("outlet_change_request.all_types")}
            searchable={false}
            matchAnchorWidth
            options={requestTypes}
            onChange={(value) => {
              setPage(0);
              setRequestType(value === null ? "" : String(value));
            }}
          />
        </Box>
        <Box width={STATUS_FILTER_WIDTH}>
          <SelectField
            value={status}
            emptyLabel={t("outlet_change_request.all_statuses")}
            placeholder={t("outlet_change_request.all_statuses")}
            searchable={false}
            matchAnchorWidth
            options={requestStatuses}
            onChange={(value) => {
              setPage(0);
              setStatus(value === null ? "" : String(value));
            }}
          />
        </Box>
        <CancelButton appSize="large" onClick={fetchRequests}>
          {t("outlet_change_request.refresh")}
        </CancelButton>
      </Box>
      <MuiTable
        header={header}
        data={rows}
        isLoading={loading}
        page={page}
        rowsPerPage={size}
        totalElements={total}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setSize(Number(event.target.value));
          setPage(0);
        }}
      />
    </Card>
  );
};

export default OutletChangeRequests;
